/**
 * Fixed hues, not theme slots. `primary` and `secondary` are the gym's own brand
 * colours, so a "blue" mapped to them showed up green in a green-branded gym.
 */
export const CLASS_COLORS = ['blue', 'purple', 'green', 'orange', 'red', 'pink', 'teal'] as const
export type ClassColor = (typeof CLASS_COLORS)[number]

/** A stored colour this app knows, or blue for anything older or unknown. */
export function classColor(value: string): ClassColor {
  return (CLASS_COLORS as readonly string[]).includes(value) ? (value as ClassColor) : 'blue'
}

export interface PersonCard {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
}

export interface ClassSeries {
  id: string
  weekdays: number[]
  start_time: string
  end_time: string
  starts_on: string
  ends_on: string | null
}

/** One class on one day. Times are gym-local wall clock: "2026-10-19T17:00:00". */
export interface ClassSession {
  id: string
  series_id: string | null
  title: string
  description: string | null
  trainer_user_id: string | null
  capacity: number
  min_age: number | null
  max_age: number | null
  /** One of CLASS_COLORS; read it through classColor(). */
  color: string
  starts_at: string
  ends_at: string
  detached: boolean
  booked: number
  trainer: PersonCard | null
  series: ClassSeries | null
}

/** A news event or a competition, shown beside the classes. Read-only here. */
export interface CalendarEvent {
  kind: 'news' | 'competition'
  id: string
  title: string
  starts_at: string
  ends_at: string | null
}

export interface ClassBooking {
  id: string
  userId: string
  childId: string | null
  createdAt: string
  /** Booked by staff with no plan behind it: they pay at the desk. */
  withoutPlan?: boolean
  profile: PersonCard | null
  child: { first_name: string; last_name: string | null } | null
}

export interface EligibleMember {
  userId: string
  childId: string | null
  profile: PersonCard
  child: { first_name: string; last_name: string | null } | null
}

/** A member found by name or phone, plan or not, for a manager to book. */
export interface FoundMember extends EligibleMember {
  phone: string | null
}

/** One day of the year overview. */
export interface DaySummary {
  classes: number
  booked: number
  capacity: number
}

type NamedPerson = Pick<PersonCard, 'first_name' | 'last_name' | 'nickname'>

/**
 * What staff call a trainer: the real name first. A nickname alone hides who
 * it is ("Sourbear22"), so it is only a fallback.
 */
export function trainerName(p: NamedPerson): string {
  const real = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
  return real || p.nickname || ''
}

/** The trainer picker: real name, nickname in brackets, email as a last resort. */
export function trainerLabel(p: NamedPerson & Pick<PersonCard, 'email'>): string {
  const name = trainerName(p)
  if (name && p.nickname && name !== p.nickname) return `${name} (${p.nickname})`
  return name || p.email || ''
}
